// src/components/dental/XrayManager.js
import React from 'react';
import { Row, Col, Card, Button, Upload, message, Modal, Tooltip, Typography } from 'antd';
import { FileImageOutlined, EyeOutlined, EditOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';

const { Title } = Typography;

const XrayManager = ({ xrayImages, setXrayImages }) => {
  const [previewVisible, setPreviewVisible] = React.useState(false);
  const [previewImage, setPreviewImage] = React.useState('');
  const [previewTitle, setPreviewTitle] = React.useState('');

  const handleXrayUpload = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.status === 'done') {
      // Get this url from response in real world.
      const url = info.file.response?.url || URL.createObjectURL(info.file.originFileObj);
      setXrayImages([...xrayImages, {
        id: Date.now(),
        name: info.file.name,
        url: url,
        date: new Date().toLocaleDateString(),
        size: info.file.size,
        type: info.file.type
      }]);
      message.success(`${info.file.name} uploaded successfully.`);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} upload failed.`);
    }
  };

  const handlePreview = (image) => {
    setPreviewImage(image.url);
    setPreviewVisible(true);
    setPreviewTitle(image.name);
  };

  const handleDelete = (id) => {
    setXrayImages(xrayImages.filter(img => img.id !== id));
    message.success('X-ray deleted successfully');
  };

  const handleDownload = (image) => {
    const link = document.createElement('a');
    link.href = image.url;
    link.download = image.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uploadProps = {
    name: 'file',
    multiple: true,
    action: '/api/upload',
    accept: 'image/*',
    onChange: handleXrayUpload,
    showUploadList: false,
  };

  return (
    <div style={{ padding: '16px' }}>
      {/* Upload Area */}
      <Card 
        title="Upload X-rays" 
        style={{ marginBottom: '24px' }}
        extra={
          <Upload {...uploadProps}>
            <Button type="primary" icon={<FileImageOutlined />}>
              Upload X-ray
            </Button>
          </Upload>
        }
      >
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          border: '2px dashed #d9d9d9',
          borderRadius: '8px',
          backgroundColor: '#fafafa'
        }}>
          <Upload.Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <FileImageOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">Click or drag X-ray images to this area to upload</p>
            <p className="ant-upload-hint">
              Support for single or bulk upload. Accepts JPG, PNG, DICOM files.
            </p>
          </Upload.Dragger>
        </div>
      </Card>

      {/* X-ray Gallery */}
      <Row gutter={[16, 16]}>
        {xrayImages.map(image => (
          <Col key={image.id} xs={24} sm={12} md={8} lg={6}>
            <Card
              hoverable
              cover={
                <div style={{ height: 200, overflow: 'hidden', cursor: 'pointer' }}>
                  <img 
                    alt={image.name} 
                    src={image.url} 
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                    onClick={() => handlePreview(image)}
                    onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  />
                </div>
              }
              actions={[
                <Tooltip title="View Full Size" key="view">
                  <EyeOutlined onClick={() => handlePreview(image)} />
                </Tooltip>,
                <Tooltip title="Download" key="download">
                  <DownloadOutlined onClick={() => handleDownload(image)} />
                </Tooltip>,
                <Tooltip title="Edit" key="edit">
                  <EditOutlined />
                </Tooltip>,
                <Tooltip title="Delete" key="delete">
                  <DeleteOutlined onClick={() => handleDelete(image.id)} />
                </Tooltip>
              ]}
            >
              <Card.Meta
                title={image.name}
                description={
                  <div>
                    <div>Uploaded: {image.date}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {image.size ? `${(image.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                    </div>
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
        
        {xrayImages.length === 0 && (
          <Col span={24}>
            <div style={{ 
              textAlign: 'center', 
              padding: '60px',
              backgroundColor: '#fafafa',
              borderRadius: '12px',
              border: '2px dashed #d9d9d9'
            }}>
              <FileImageOutlined style={{ fontSize: '48px', color: '#bfbfbf' }} />
              <Title level={4} style={{ marginTop: '16px', color: '#8c8c8c' }}>
                No X-rays uploaded yet
              </Title>
              <p style={{ color: '#8c8c8c' }}>
                Upload X-ray images to enhance patient documentation and diagnosis
              </p>
            </div>
          </Col>
        )}
      </Row>

      {/* Preview Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="80%"
        style={{ top: 20 }}
      >
        <img alt="X-ray preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default XrayManager;